import styled from "styled-components";
import { themeGet } from "@styled-system/theme-get";
import { Badge, Box, Flex, Icon, Small, Text } from "orcs-design-system";

import {
  formatElapsedAgo,
  liveElapsedHours,
  useClockNow,
} from "src/pages/WorkforceGuardrails/clockHours";

import type { InboxItem } from "./inboxItem";

/** Selection is a primary left rail plus a primary ring. Hover is the surface tint. */
const CardButton = styled(Box)`
  appearance: none;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-align: left;
  box-shadow: inset 3px 0 0 transparent;
  transition:
    box-shadow 120ms ease,
    border-color 120ms ease,
    background-color 120ms ease;

  &:hover {
    background-color: ${themeGet("colors.hoverSurface")};
  }

  &:focus-visible {
    outline: none;
    border-color: ${themeGet("colors.focusOutline")};
    box-shadow:
      inset 3px 0 0 transparent,
      ${themeGet("shadows.thickOutline")} ${themeGet("colors.primary30")};
  }

  &[aria-current="true"] {
    border-color: ${themeGet("colors.primary")};
    box-shadow: inset 3px 0 0 ${themeGet("colors.primary")};
  }

  &[aria-current="true"]:focus-visible {
    border-color: ${themeGet("colors.focusOutline")};
    box-shadow:
      inset 3px 0 0 ${themeGet("colors.primary")},
      ${themeGet("shadows.thickOutline")} ${themeGet("colors.primary30")};
  }
`;

const capitalise = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

const InboxListCard = ({
  item,
  selected,
  onSelect,
}: {
  item: InboxItem;
  selected: boolean;
  onSelect: (key: string) => void;
}) => {
  const nowMs = useClockNow();
  const age = formatElapsedAgo(
    liveElapsedHours(item.elapsedHours, item.clockStartedAt, nowMs)
  );
  return (
    <CardButton
      forwardedAs="button"
      type="button"
      width="100%"
      p="s"
      bg="surface"
      boxBorder="default"
      borderRadius={2}
      aria-current={selected ? "true" : undefined}
      data-testid={`inbox-card-${item.requisitionId}`}
      onClick={() => onSelect(item.key)}
    >
      <Flex gap="xs" alignItems="center" flexWrap="wrap" mb="xs">
        <Badge variant="primaryLight">Approval</Badge>
        {item.flags.template ? (
          <Badge variant="warning">{capitalise(item.flags.template)}</Badge>
        ) : null}
        {item.flags.pastSla ? <Badge variant="danger">Past SLA</Badge> : null}
        <Small color="greyDark" ml="auto">
          <Icon icon={["far", "clock"]} mr="xs" />
          {age}
        </Small>
      </Flex>
      <Text weight="bold" display="block">
        {item.title}
      </Text>
      {item.subtitle ? (
        <Small color="greyDark" display="block">
          {item.subtitle}
        </Small>
      ) : null}
      <Small display="block" mt="xs">
        Approving as {item.step}
      </Small>
      {item.failingChecks.map((label) => (
        <Small key={label} color="warningDarker" display="block">
          <Icon icon={["far", "exclamation-triangle"]} mr="xs" />
          {label}
        </Small>
      ))}
    </CardButton>
  );
};

export default InboxListCard;
